// ============================================
// pages/UserProfile.tsx
//
// 用户主页. URL: /u/:shortId
//
// 接后端 (Week 2 已就绪):
//   community-get-public-profile  → 用户基本信息
//   community-list-projects (author_short_id 筛选) → 作品列表 (含 next_cursor 分页)
//
// Week 2 重做要点 (官网设计系统):
//   - 套 Layout
//   - 资料卡: 大头像 + 昵称 + 等级/角色徽章 + bio + 加入时间
//   - 数据条: 作品 / 关注 / 粉丝 / 获赞 (官网克制蓝系, 不用白字渐变顶栏)
//   - 作品网格复用 ProjectCard, 游标分页加载更多
// ============================================

import { useEffect, useState, useCallback } from 'react';
import { Search, User, Puzzle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ProjectCard from '../components/ProjectCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorPage from '../components/ErrorPage';
import Avatar from '../components/Avatar';
import FollowListModal from '../components/FollowListModal';
import { callApi } from '../services/api';
import { getCachedUser, authedCallApi } from '../services/auth';
import { requireLogin } from '../services/loginPrompt';
import type { ProjectPublic } from '../types/community';

interface PublicProfile {
  short_id: string;
  nickname: string;
  avatar_url: string | null;
  bio: string;
  role: string;
  level: number;
  project_count: number;
  followers_count: number;
  following_count: number;
  total_likes_received: number;
  joined_at: number;
  is_following?: boolean;
  is_me?: boolean;
}

const ROLE_BADGE: Record<string, { label: string; cls: string }> = {
  verified_teacher: { label: '认证老师', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  official: { label: '官方', cls: 'bg-gold-600 text-white border-gold-600' },
};

export default function UserProfile() {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();
  const isMe = (() => {
    const me = getCachedUser();
    return !!(me && me.short_id && me.short_id === shortId);
  })();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [projects, setProjects] = useState<ProjectPublic[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [followHover, setFollowHover] = useState(false);   // 已关注按钮 hover → 显示"取消关注"
  const [followModal, setFollowModal] = useState<'following' | 'followers' | null>(null);

  const fetchProjects = useCallback(
    async (reset: boolean, cur: string | null) => {
      if (!shortId) return;
      if (!reset) setLoadingMore(true);
      const r = await callApi<{ list: ProjectPublic[]; next_cursor: string | null }>(
        'community-list-projects',
        { author_short_id: shortId, cursor: cur },
      );
      if (r.ok && Array.isArray(r.list)) {
        const incoming = r.list as ProjectPublic[];
        setProjects((prev) => (reset ? incoming : [...prev, ...incoming]));
        setCursor((r.next_cursor as string | null) ?? null);
        setHasMore(Boolean(r.next_cursor));
      }
      if (!reset) setLoadingMore(false);
    },
    [shortId],
  );

  const toggleFollow = async () => {
    if (!requireLogin()) return;
    if (followBusy || !shortId) return;
    setFollowBusy(true);
    // 乐观更新
    const next = !following;
    setFollowing(next);
    setFollowerCount((c) => c + (next ? 1 : -1));
    const r = await authedCallApi<{ following: boolean }>('community-follow-user', {
      target_short_id: shortId,
      op: next ? 'follow' : 'unfollow',
    });
    setFollowBusy(false);
    if (!r.ok) {
      // 失败回滚
      setFollowing(!next);
      setFollowerCount((c) => c + (next ? -1 : 1));
    }
  };

  useEffect(() => {
    if (!shortId) return;
    setLoading(true);
    setError(null);
    setCursor(null);

    Promise.all([
      callApi<{ profile: PublicProfile }>('community-get-public-profile', { short_id: shortId }),
      fetchProjects(true, null),
    ])
      .then(([profileR]) => {
        if (!profileR.ok) {
          setError(profileR.message || profileR.error || '用户不存在');
          return;
        }
        setProfile(profileR.profile as PublicProfile);
        setFollowing(!!(profileR.profile as PublicProfile).is_following);
        setFollowerCount((profileR.profile as PublicProfile).followers_count || 0);
      })
      .finally(() => setLoading(false));
  }, [shortId, fetchProjects]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !profile) {
    return (
      <ErrorPage icon={<Search className="w-12 h-12" strokeWidth={1.5} />} title="用户不存在" message={error || '可能已被注销或链接错误。'} fullscreenGradient={false} />
    );
  }

  const roleBadge = ROLE_BADGE[profile.role];

  return (
    <Layout width="wide">
      {/* ── 资料卡 ── */}
      <section className="pt-10">
        <div className="bg-white border border-ink-300/40 rounded-3xl p-6 sm:p-8 shadow-card relative overflow-hidden">
          {/* 顶部装饰渐变带 */}
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-br from-gold-soft to-gold-100/40 pointer-events-none" />

          <div className="relative flex flex-col sm:flex-row sm:items-end gap-5">
            <div className="border-4 border-white shadow-card rounded-2xl">
              <Avatar name={profile.nickname} size={96} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-black text-ink-900 truncate">{profile.nickname}</h1>
                <span className="pill bg-gold-soft text-gold-700">LV{profile.level}</span>
                {roleBadge && (
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${roleBadge.cls}`}>
                    {roleBadge.label}
                  </span>
                )}
              </div>
              {profile.bio ? (
                <p className="text-sm text-ink-600 mt-2 leading-relaxed max-w-xl">{profile.bio}</p>
              ) : (
                <p className="text-sm text-ink-400 mt-2 italic">这个人很神秘,什么也没留下</p>
              )}
              <p className="text-xs text-ink-400 mt-2 font-mono">
                加入于 {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString('zh-CN') : '—'}
              </p>
              {isMe ? (
                <button
                  onClick={() => navigate('/settings/profile')}
                  className="mt-3 px-4 py-1.5 rounded-lg bg-ink-100 text-ink-700 text-sm font-medium hover:bg-ink-200 transition-colors"
                >
                  编辑资料
                </button>
              ) : (
                <button
                  onClick={toggleFollow}
                  disabled={followBusy}
                  onMouseEnter={() => setFollowHover(true)}
                  onMouseLeave={() => setFollowHover(false)}
                  className={`mt-3 px-5 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 ${
                    following
                      ? (followHover
                          ? 'bg-red-50 text-red-500 border border-red-200'
                          : 'bg-ink-100 text-ink-600')
                      : 'bg-gold-500 text-white hover:bg-gold-600'
                  }`}
                >
                  {following ? (followHover ? '取消关注' : '已关注') : '+ 关注'}
                </button>
              )}
            </div>
          </div>

          {/* 数据条 */}
          <div className="relative grid grid-cols-4 gap-3 mt-7 pt-6 border-t border-ink-300/30">
            <Stat label="作品" value={hasMore ? `${projects.length}+` : projects.length} />
            <Stat label="关注" value={profile.following_count} onClick={isMe ? () => setFollowModal('following') : undefined} />
            <Stat label="粉丝" value={followerCount} onClick={isMe ? () => setFollowModal('followers') : undefined} />
            <Stat label="获赞" value={profile.total_likes_received} />
          </div>
        </div>
      </section>

      {/* ── 作品 ── */}
      <section className="py-8">
        <div className="flex items-center gap-3 mb-5">
          <span className="section-num">WORKS</span>
          <h2 className="text-lg font-bold text-ink-900">
            作品 {projects.length > 0 && <span className="text-ink-400 font-normal">· {hasMore ? `${projects.length}+` : projects.length}</span>}
          </h2>
        </div>

        {projects.length === 0 ? (
          <div className="bg-white border border-ink-300/40 rounded-2xl py-16 text-center">
            <div className="flex justify-center mb-3"><Puzzle className="w-10 h-10 text-gold-300" strokeWidth={1.5} /></div>
            <p className="text-ink-500 text-sm">这位创作者还没有发布作品</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((p) => (
                <ProjectCard key={p.short_id} project={p} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-10">
                <button className="btn-secondary" disabled={loadingMore} onClick={() => fetchProjects(false, cursor)}>
                  {loadingMore ? '加载中…' : '加载更多'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
      {followModal && shortId && (
        <FollowListModal
          shortId={shortId}
          kind={followModal}
          onClose={() => setFollowModal(null)}
        />
      )}
    </Layout>
  );
}

function Stat({ label, value, onClick }: { label: string; value: number | string; onClick?: () => void }) {
  return (
    <div
      className={`text-center ${onClick ? 'cursor-pointer group' : ''}`}
      onClick={onClick}
    >
      <div className={`text-2xl font-black text-ink-900 tabular-nums ${onClick ? 'group-hover:text-gold-600 transition-colors' : ''}`}>{fmt(value)}</div>
      <div className={`text-xs text-ink-400 mt-0.5 ${onClick ? 'group-hover:text-gold-500' : ''}`}>{label}</div>
    </div>
  );
}

function fmt(n: number | string): string {
  if (typeof n === 'string') return n;
  if (n < 1000) return String(n);
  if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return Math.round(n / 1000) + 'k';
}
